import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { confirmArrival } from "@/lib/shifts.functions";
import { confirmShiftCompletion, cancelShift } from "@/lib/payments.functions";
import { getPayRate } from "@/lib/pay-rates";
import { getStripeEnvironment } from "@/lib/stripe";
import { flowBreadcrumb, setFlowSubject } from "@/lib/breadcrumbs";
import { reportCancellationFailure } from "@/lib/cancellation-error";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { CancelShiftDialog, type CancelReason } from "@/components/CancelShiftDialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/bartender/engagements")({
  head: () => ({ meta: [{ title: "Active Engagements — The Hospitality Reserve" }] }),
  component: BartenderEngagements,
});

type Row = {
  booking_id: string;
  shift_id: string;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  role_type: string;
  address: string | null;
  postcode: string | null;
  notes: string | null;
  bartender_confirmed_at: string | null;
  venue_confirmed_at: string | null;
  arrival_venue_confirmed_at: string | null;
  arrival_bartender_confirmed_at: string | null;
  venue_name: string | null;
};

function BartenderEngagements() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [arrivalBusyId, setArrivalBusyId] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelShiftId, setCancelShiftId] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const navigate = useNavigate();

  const arrivalFn = useServerFn(confirmArrival);
  const confirmFn = useServerFn(confirmShiftCompletion);
  const cancelFn = useServerFn(cancelShift);

  const load = async () => {
    setLoading(true);
    const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
    if (!u.user) { navigate({ to: "/auth" }); return; }
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        id, shift_id, arrival_venue_confirmed_at, arrival_bartender_confirmed_at,
        shift:shifts!inner(
          id, start_time, end_time, status, payment_status, role_type,
          address, postcode, notes, venue_confirmed_at, bartender_confirmed_at, venue_id
        )
      `)
      .eq("bartender_id", u.user.id)
      .eq("status", "accepted");
    if (error) { toast.error(error.message); setLoading(false); return; }
    const records = (data ?? []) as unknown as Array<{
      id: string;
      shift_id: string;
      arrival_venue_confirmed_at: string | null;
      arrival_bartender_confirmed_at: string | null;
      shift: {
        id: string; start_time: string; end_time: string; status: string;
        payment_status: string; role_type: string; address: string | null;
        postcode: string | null; notes: string | null;
        venue_confirmed_at: string | null; bartender_confirmed_at: string | null;
        venue_id: string;
      };
    }>;
    const venueIds = Array.from(new Set(records.map((r) => r.shift.venue_id)));
    const venueNames = new Map<string, string>();
    if (venueIds.length) {
      const { data: venues } = await supabase
        .from("profiles").select("id,full_name").in("id", venueIds);
      (venues ?? []).forEach((v) => {
        venueNames.set(v.id, v.full_name || "Venue");
      });
    }
    const mapped: Row[] = records
      .filter((r) => !["completed", "cancelled"].includes(r.shift.status))
      .map((r) => ({
        booking_id: r.id,
        shift_id: r.shift_id,
        start_time: r.shift.start_time,
        end_time: r.shift.end_time,
        status: r.shift.status,
        payment_status: r.shift.payment_status,
        role_type: r.shift.role_type,
        address: r.shift.address,
        postcode: r.shift.postcode,
        notes: r.shift.notes,
        bartender_confirmed_at: r.shift.bartender_confirmed_at,
        venue_confirmed_at: r.shift.venue_confirmed_at,
        arrival_venue_confirmed_at: r.arrival_venue_confirmed_at,
        arrival_bartender_confirmed_at: r.arrival_bartender_confirmed_at,
        venue_name: venueNames.get(r.shift.venue_id) ?? null,
      }))
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    setRows(mapped);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const handleArrival = async (row: Row) => {
    setArrivalBusyId(row.booking_id);
    try {
      const res = await arrivalFn({ data: { booking_id: row.booking_id } });
      toast.success(res.bothConfirmed ? "Both sides confirmed — payment guaranteed." : "Arrival recorded. Awaiting venue.");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm arrival."); }
    finally { setArrivalBusyId(null); }
  };

  const handleConfirm = async (row: Row) => {
    setActingId(row.shift_id);
    try {
      const res = await confirmFn({ data: { shift_id: row.shift_id } });
      toast.success(res.completed ? "Engagement closed and payout queued." : "Confirmed. Awaiting venue.");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm."); }
    finally { setActingId(null); }
  };

  const openCancel = (shiftId: string) => { setCancelShiftId(shiftId); setCancelOpen(true); };
  const doCancel = async (reason: CancelReason, reason_text: string) => {
    if (!cancelShiftId) return;
    setCancelOpen(false);
    setActingId(cancelShiftId);
    setFlowSubject({ shiftId: cancelShiftId });
    flowBreadcrumb("cancellation", "cancel requested", { shift_id: cancelShiftId, role: "bartender", reason });
    try {
      const res = await cancelFn({ data: { shift_id: cancelShiftId, environment: getStripeEnvironment(), reason, reason_text: reason_text || undefined } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Engagement cancelled.");
      await load();
    } catch (err) {
      const t = reportCancellationFailure(err, { shift_id: cancelShiftId, role: "bartender", reason });
      toast.error(t.title, { description: t.description, duration: 12000 });
    }
    finally { setActingId(null); setCancelShiftId(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Live</p>
            <h1 className="mt-3 font-display text-3xl">Active Engagements</h1>
            <p className="mt-2 text-sm text-muted-foreground">Confirm arrival, message the venue, and close out the shift.</p>
          </div>
          <Link to="/bartender" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back to feed</Link>
        </div>
        <span className="gold-divider mt-4" />

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="mt-10 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No active engagements right now. When a venue accepts you, the shift will appear here.
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {rows.map((row) => {
              const start = new Date(row.start_time);
              const end = new Date(row.end_time);
              const rate = getPayRate(row.role_type);
              const inArrivalWindow =
                row.status === "paid" &&
                start.getTime() - nowTick <= 2 * 3_600_000 &&
                end.getTime() >= nowTick;
              const bothArrived = !!(row.arrival_venue_confirmed_at && row.arrival_bartender_confirmed_at);
              const canConfirm = row.status === "paid" && end.getTime() <= nowTick && !row.bartender_confirmed_at;
              const canCancel = ["open", "awaiting_payment", "paid"].includes(row.status) && start.getTime() > nowTick;
              const live = start.getTime() <= nowTick && end.getTime() >= nowTick;

              return (
                <article key={row.booking_id} className="border border-border bg-card p-6">
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-display text-xl">
                          {start.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                        </p>
                        {live && <span className="border border-emerald-600 text-emerald-700 px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">LIVE NOW</span>}
                        <span className="border border-border text-muted-foreground px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">{rate.label}</span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} — {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                      {row.venue_name && <p className="mt-2 text-sm">{row.venue_name}</p>}
                      {(row.address || row.postcode) && (
                        <p className="mt-1 text-xs text-muted-foreground">📍 {[row.address, row.postcode].filter(Boolean).join(" · ")}</p>
                      )}
                      {row.notes && <p className="mt-3 text-sm text-foreground/80">{row.notes}</p>}

                      <div className="mt-4 flex flex-wrap gap-3 text-[0.7rem] tracking-wide-luxury">
                        <span className={row.arrival_bartender_confirmed_at ? "text-emerald-700" : "text-muted-foreground"}>
                          {row.arrival_bartender_confirmed_at ? "✓ You arrived" : "· Awaiting your arrival"}
                        </span>
                        <span className={row.arrival_venue_confirmed_at ? "text-emerald-700" : "text-muted-foreground"}>
                          {row.arrival_venue_confirmed_at ? "✓ Venue confirmed you" : "· Awaiting venue confirm"}
                        </span>
                        {bothArrived && <span className="text-accent">★ Payment guaranteed</span>}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Link
                        to="/shift/$id/notes"
                        params={{ id: row.shift_id }}
                        className="border border-accent text-accent px-3 py-2 tracking-wide-luxury text-[0.65rem] hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        Message Venue →
                      </Link>

                      {inArrivalWindow && (
                        <Button
                          onClick={() => handleArrival(row)}
                          disabled={arrivalBusyId === row.booking_id || !!row.arrival_bartender_confirmed_at}
                          className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
                        >
                          {row.arrival_bartender_confirmed_at ? "Arrival Confirmed" : arrivalBusyId === row.booking_id ? "…" : "I've Arrived"}
                        </Button>
                      )}

                      {canConfirm && (
                        <Button
                          onClick={() => handleConfirm(row)}
                          disabled={actingId === row.shift_id}
                          className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury"
                        >
                          {actingId === row.shift_id ? "…" : "Confirm Completion"}
                        </Button>
                      )}

                      {canCancel && (
                        <Button
                          onClick={() => openCancel(row.shift_id)}
                          disabled={actingId === row.shift_id}
                          variant="outline"
                          className="rounded-none border-border tracking-wide-luxury text-xs"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      <CancelShiftDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={doCancel}
        busy={!!actingId}
        policyText="You'll be removed from this engagement and refunded if already paid."
      />
    </div>
  );
}
